/**
 * Pure-TypeScript ISO/IEC 18004 QR Code SVG Generator (Versions 1..6, Error Correction Level L, Byte Mode).
 * Zero external dependencies, zero third-party image/tracking requests — safe for private session pairing URLs!
 */

interface QrVersionSpec {
  version: number;
  size: number;
  totalCodewords: number;
  ecCodewordsPerBlock: number;
  numBlocks: number;
  alignmentCenters: number[];
}

const QR_VERSIONS_L: QrVersionSpec[] = [
  { version: 1, size: 21, totalCodewords: 26, ecCodewordsPerBlock: 7, numBlocks: 1, alignmentCenters: [] },
  { version: 2, size: 25, totalCodewords: 44, ecCodewordsPerBlock: 10, numBlocks: 1, alignmentCenters: [6, 18] },
  { version: 3, size: 29, totalCodewords: 70, ecCodewordsPerBlock: 15, numBlocks: 1, alignmentCenters: [6, 22] },
  { version: 4, size: 33, totalCodewords: 100, ecCodewordsPerBlock: 20, numBlocks: 1, alignmentCenters: [6, 26] },
  { version: 5, size: 37, totalCodewords: 134, ecCodewordsPerBlock: 26, numBlocks: 1, alignmentCenters: [6, 30] },
  { version: 6, size: 41, totalCodewords: 172, ecCodewordsPerBlock: 18, numBlocks: 2, alignmentCenters: [6, 34] },
];

// GF(256) log/exp tables with primitive polynomial 0x11d
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) {
    GF_EXP[i] = GF_EXP[i - 255];
  }
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[GF_LOG[a] + GF_LOG[b]];
}

function reedSolomonDivisor(degree: number): Uint8Array {
  let result = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(result.length + 1);
    const factor = GF_EXP[i];
    for (let j = 0; j < result.length; j++) {
      next[j] ^= result[j];
      next[j + 1] ^= gfMul(result[j], factor);
    }
    result = next;
  }
  return result.slice(1);
}

function reedSolomonRemainder(data: Uint8Array, ecLen: number): Uint8Array {
  const divisor = reedSolomonDivisor(ecLen);
  const rem = new Uint8Array(ecLen);
  for (let i = 0; i < data.length; i++) {
    const factor = data[i] ^ rem[0];
    rem.copyWithin(0, 1);
    rem[ecLen - 1] = 0;
    for (let j = 0; j < ecLen; j++) {
      rem[j] ^= gfMul(divisor[j], factor);
    }
  }
  return rem;
}

function encodeDataCodewords(bytes: Uint8Array, spec: QrVersionSpec): Uint8Array {
  const dataCapacity = spec.totalCodewords - spec.ecCodewordsPerBlock * spec.numBlocks;
  const bits: number[] = [];
  const pushBits = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1);
  };

  // Mode indicator 0100 (Byte mode) + 8-bit length (for V1..9)
  pushBits(0b0100, 4);
  pushBits(bytes.length, 8);
  for (const b of bytes) pushBits(b, 8);

  const maxBits = dataCapacity * 8;
  const terminator = Math.min(4, maxBits - bits.length);
  pushBits(0, terminator);
  while (bits.length % 8 !== 0) bits.push(0);

  const dataWords = new Uint8Array(dataCapacity);
  let idx = 0;
  for (let i = 0; i < bits.length; i += 8) {
    let w = 0;
    for (let b = 0; b < 8; b++) w = (w << 1) | bits[i + b];
    dataWords[idx++] = w;
  }
  let padToggle = true;
  while (idx < dataCapacity) {
    dataWords[idx++] = padToggle ? 0xec : 0x11;
    padToggle = !padToggle;
  }

  if (spec.numBlocks === 1) {
    const ec = reedSolomonRemainder(dataWords, spec.ecCodewordsPerBlock);
    const out = new Uint8Array(spec.totalCodewords);
    out.set(dataWords, 0);
    out.set(ec, dataWords.length);
    return out;
  }

  // Interleave equal-sized blocks (V6-L has 2 equal blocks of 68 data + 18 EC)
  const blockDataLen = dataCapacity / spec.numBlocks;
  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  for (let b = 0; b < spec.numBlocks; b++) {
    const slice = dataWords.slice(b * blockDataLen, (b + 1) * blockDataLen);
    dataBlocks.push(slice);
    ecBlocks.push(reedSolomonRemainder(slice, spec.ecCodewordsPerBlock));
  }
  const out = new Uint8Array(spec.totalCodewords);
  let pos = 0;
  for (let i = 0; i < blockDataLen; i++) {
    for (let b = 0; b < spec.numBlocks; b++) out[pos++] = dataBlocks[b][i];
  }
  for (let i = 0; i < spec.ecCodewordsPerBlock; i++) {
    for (let b = 0; b < spec.numBlocks; b++) out[pos++] = ecBlocks[b][i];
  }
  return out;
}

/**
 * Generates an ISO/IEC 18004 QR Code matrix for `text` (up to 134 UTF-8 bytes).
 */
export function encodeQrMatrix(text: string): boolean[][] {
  const bytes = new TextEncoder().encode(text);
  const spec =
    QR_VERSIONS_L.find((s) => {
      const cap = s.totalCodewords - s.ecCodewordsPerBlock * s.numBlocks - 2;
      return bytes.length <= cap;
    }) ?? QR_VERSIONS_L[QR_VERSIONS_L.length - 1];

  const payload = bytes.length > 134 ? bytes.slice(0, 134) : bytes;
  const codewords = encodeDataCodewords(payload, spec);
  const n = spec.size;
  const modules: boolean[][] = Array.from({ length: n }, () => Array(n).fill(false));
  const isFunc: boolean[][] = Array.from({ length: n }, () => Array(n).fill(false));

  const setFunc = (x: number, y: number, dark: boolean) => {
    if (x < 0 || y < 0 || x >= n || y >= n) return;
    modules[y][x] = dark;
    isFunc[y][x] = true;
  };

  // Finder patterns + separators
  const drawFinder = (cx: number, cy: number) => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        setFunc(cx + dx, cy + dy, dist !== 2 && dist !== 4);
      }
    }
  };
  drawFinder(3, 3);
  drawFinder(n - 4, 3);
  drawFinder(3, n - 4);

  // Timing patterns
  for (let i = 0; i < n; i++) {
    if (!isFunc[6][i]) setFunc(i, 6, i % 2 === 0);
    if (!isFunc[i][6]) setFunc(6, i, i % 2 === 0);
  }

  // Alignment patterns
  const centers = spec.alignmentCenters;
  for (let i = 0; i < centers.length; i++) {
    for (let j = 0; j < centers.length; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === centers.length - 1) || (i === centers.length - 1 && j === 0)) {
        continue;
      }
      const cx = centers[i];
      const cy = centers[j];
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          setFunc(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
        }
      }
    }
  }

  // Reserve format info modules & dark module
  for (let i = 0; i <= 8; i++) {
    if (i !== 6) {
      setFunc(8, i, false);
      setFunc(i, 8, false);
    }
  }
  for (let i = 0; i < 8; i++) {
    setFunc(n - 1 - i, 8, false);
    setFunc(8, n - 1 - i, false);
  }
  setFunc(8, n - 8, true);

  // Zigzag codeword placement + Mask 0 ((x + y) % 2 === 0)
  let bitIdx = 0;
  const totalBits = codewords.length * 8;
  for (let right = n - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < n; vert++) {
      const upward = ((right + 1) & 2) === 0;
      const y = upward ? n - 1 - vert : vert;
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        if (!isFunc[y][x]) {
          let dark = false;
          if (bitIdx < totalBits) {
            dark = ((codewords[bitIdx >>> 3] >>> (7 - (bitIdx & 7))) & 1) === 1;
            bitIdx++;
          }
          if ((x + y) % 2 === 0) dark = !dark;
          modules[y][x] = dark;
        }
      }
    }
  }

  // Format bits for Level L (01) + Mask 0 (000) => precomputed BCH = 0x77c4
  const formatBits = 0x77c4;
  const getFmtBit = (i: number) => ((formatBits >>> i) & 1) === 1;
  for (let i = 0; i <= 5; i++) setFunc(8, i, getFmtBit(i));
  setFunc(8, 7, getFmtBit(6));
  setFunc(8, 8, getFmtBit(7));
  setFunc(7, 8, getFmtBit(8));
  for (let i = 9; i < 15; i++) setFunc(14 - i, 8, getFmtBit(i));

  for (let i = 0; i < 8; i++) setFunc(n - 1 - i, 8, getFmtBit(i));
  for (let i = 8; i < 15; i++) setFunc(8, n - 15 + i, getFmtBit(i));
  setFunc(8, n - 8, true);

  return modules;
}

/**
 * Renders a crisp, self-contained `<svg>` QR code string for pairing companion devices.
 */
export function generatePairingQrSvg(url: string, margin = 3): string {
  const matrix = encodeQrMatrix(url);
  const n = matrix.length;
  const total = n + margin * 2;
  const rects: string[] = [];
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (matrix[y][x]) {
        rects.push(`M${x + margin},${y + margin}h1v1h-1z`);
      }
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${total}" shape-rendering="crispEdges" role="img" aria-label="Companion Pairing QR Code"><rect width="${total}" height="${total}" fill="#ffffff"/><path d="${rects.join('')}" fill="#090d16"/></svg>`;
}
